/**
 * 動画ギャラリー（共通コンポーネント）。
 * storage/marketing/lp/videos/ 配下の既存動画を一覧表示 + 新規アップロード可能。
 * （旧 landing/videos は 2026-05-11 マイグレで marketing/lp/videos に移行）
 *
 * ImageGalleryDialog と同パターン。動画固有の処理:
 * - MIME: video/*
 * - グリッドサムネイルは <video preload="metadata"> で先頭フレーム表示
 * - サイズ上限デフォルト 300MB
 */
import { Trash2, Upload } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  deleteObject,
  getDownloadURL,
  listAll,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { storage } from '@/lib/firebase';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 選択された動画 URL を返す */
  onSelect: (url: string) => void;
  /** モーダルタイトル */
  title?: string;
  /** ストレージパス（デフォルト marketing/lp/videos） */
  storagePath?: string;
  /** 最大バイト数（デフォルト 300MB） */
  maxBytes?: number;
  /** サイズ制約の説明文 */
  hintText?: string;
}

interface VideoItem {
  fullPath: string;
  url: string;
  name: string;
}

const STORAGE_PATH_DEFAULT = 'marketing/lp/videos';
const DEFAULT_MAX_BYTES = 300 * 1024 * 1024;

async function listVideos(path: string): Promise<VideoItem[]> {
  const ref = storageRef(storage, path);
  const result = await listAll(ref);
  const items = await Promise.all(
    result.items.map(async (it) => {
      const url = await getDownloadURL(it);
      return { fullPath: it.fullPath, url, name: it.name };
    }),
  );
  return items.sort((a, b) => b.name.localeCompare(a.name));
}

export function VideoGalleryDialog({
  open,
  onOpenChange,
  onSelect,
  title = '動画を選択',
  storagePath = STORAGE_PATH_DEFAULT,
  maxBytes = DEFAULT_MAX_BYTES,
  hintText,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await listVideos(storagePath);
      setVideos(list);
    } catch (err) {
      toast.error(`動画一覧の取得失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      void refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, storagePath]);

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith('video/')) {
      toast.error('動画ファイルを選択してください');
      return;
    }
    if (file.size > maxBytes) {
      toast.error(
        `${(maxBytes / 1024 / 1024).toFixed(0)}MB 以下の動画を選択してください（現 ${(file.size / 1024 / 1024).toFixed(1)} MB）`,
      );
      return;
    }
    setUploading(true);
    try {
      const safe = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const fileName = `${Date.now()}-${safe}`;
      const targetRef = storageRef(storage, `${storagePath}/${fileName}`);
      await uploadBytes(targetRef, file, { contentType: file.type });
      toast.success('アップロードしました');
      await refresh();
    } catch (err) {
      toast.error(`アップロード失敗: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (item: VideoItem, e: React.MouseEvent) => {
    e.stopPropagation();
    if (
      !window.confirm(
        'この動画を削除します。共有 URL を使っている他のランディングに影響する可能性があります。',
      )
    )
      return;
    try {
      const targetRef = storageRef(storage, item.fullPath);
      await deleteObject(targetRef);
      toast.success('削除しました');
      await refresh();
    } catch (err) {
      toast.error(`削除失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {hintText ??
              `既存の動画から選ぶか、新しい動画をアップロードしてください（${(maxBytes / 1024 / 1024).toFixed(0)}MB 以下）。`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-xs text-[var(--text-muted)]">
              {loading ? '読み込み中…' : `${videos.length} 件`}
            </div>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? <Spinner className="size-3 mr-1" /> : <Upload className="size-3.5 mr-1" />}
              新規アップロード
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void handleUpload(f);
              }}
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center p-12">
              <Spinner />
            </div>
          ) : videos.length === 0 ? (
            <div
              className="border-2 border-dashed border-[var(--border)] rounded-lg p-12 text-center cursor-pointer hover:border-[var(--accent)] transition-colors"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) void handleUpload(f);
              }}
            >
              <Upload className="size-10 mx-auto text-[var(--text-muted)] mb-2" />
              <div className="text-sm text-[var(--text-secondary)]">
                動画がまだありません
              </div>
              <div className="text-xs text-[var(--text-muted)] mt-1">
                クリックまたはドラッグ&ドロップでアップロード
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-[480px] overflow-y-auto">
              {videos.map((vid) => (
                <button
                  key={vid.fullPath}
                  type="button"
                  onClick={() => {
                    onSelect(vid.url);
                    onOpenChange(false);
                  }}
                  className="relative group aspect-video rounded border border-[var(--border)] hover:border-[var(--accent)] overflow-hidden bg-black"
                  title={vid.name}
                >
                  <video
                    src={vid.url}
                    preload="metadata"
                    muted
                    playsInline
                    className="w-full h-full object-cover"
                  />
                  <span
                    onClick={(e) => void handleDelete(vid, e)}
                    className="absolute top-1 right-1 size-6 rounded-full bg-[var(--accent-red)] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                    title="削除"
                  >
                    <Trash2 className="size-3" />
                  </span>
                  <span className="absolute bottom-1 left-1 right-1 text-[10px] text-white bg-black/60 px-1 py-0.5 rounded truncate">
                    {vid.name}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            キャンセル
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
